import {
  applicantSection,
  deMinimisAttachments,
  feeInfo,
  otherAttachments,
  penaltyDeclaration,
  replySection,
} from "modules/ebiForms/common";
import {
  SECURITY_DOCUMENT,
  SURETY,
  expensesField,
  securityField,
} from "modules/ebiForms/definitions/sharedFamilyLabour";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * H6. Wniosek o przyznanie jednorazowo środków na podjęcie działalności gospodarczej — art. 147–153 ustawy z 20.03.2025 r.
 * o rynku pracy i służbach zatrudnienia (klauzula karna: art. 149 ust. 2); treść wniosku, oświadczenia i dokumenty:
 * § 2–3 rozporządzenia MRPiPS z 21.11.2025 r. (Dz.U. poz. 1645), zabezpieczenia: § 10 (teksty z api.sejm.gov.pl pobrane
 * 2026-10-22). Formularz ustala PUP; układ jak wzór PUP Złotoryja 2026 —
 * https://zlotoryja.praca.gov.pl/documents/d/powiatowy-urzad-pracy-w-zlotoryi/2026-wniosek-o-przyznanie-jednorazowo-srodkow-na-podjecie-dzialalnosci-gospodarczej-4-pdf
 * (pobrany 2026-10-22): dane, opis działalności, przychody i koszty, specyfikacja wydatków (lista pozycji zamiast tabeli),
 * zabezpieczenie, oświadczenia. Zakres oświadczeń zależy od kategorii wnioskodawcy (§ 2 ust. 4–6). Bez części
 * pełnomocnika — oświadczenia składa osobiście wnioskodawca. Załączniki z praktyki PUP nieobowiązkowe (do potwierdzenia
 * z urzędem); dokument ustanowienia zabezpieczenia dostarcza się przed zawarciem umowy.
 */
const UNEMPLOYED: EbiCondition = { field: "kategoria", equals: "bezrobotny" };
const GRADUATE: EbiCondition = { field: "kategoria", equals: "absolwent" };
const CARER: EbiCondition = { field: "kategoria", equals: "opiekun" };
const NOT_MOBILE: EbiCondition = { not: { field: "mobilna", equals: true } };

const H6: IEbiFormDefinition = {
  code: "H6",
  version: 1,
  title: "Wniosek o przyznanie jednorazowo środków na podjęcie działalności gospodarczej",
  category: EEbiFormCategory.Labour,
  office: EEbiFormOffice.CountyUnit,
  description:
    "Dofinansowanie podjęcia działalności gospodarczej z Funduszu Pracy dla bezrobotnego, absolwenta CIS/KIS albo opiekuna osoby niepełnosprawnej — w naborach PUP.",
  keywords: ["dotacja", "jednorazowe środki", "działalność gospodarcza", "firma", "bezrobotny", "PUP", "Fundusz Pracy", "dofinansowanie"],
  organ: "Starosta — Powiatowy Urząd Pracy właściwy według miejsca zamieszkania wnioskodawcy",
  legalBasis:
    "art. 147–153 ustawy z dnia 20 marca 2025 r. o rynku pracy i służbach zatrudnienia; § 2–4 i § 10 rozporządzenia Ministra Rodziny, Pracy i Polityki Społecznej z dnia 21 listopada 2025 r. w sprawie wniosków i realizacji umów o dofinansowanie podjęcia działalności gospodarczej oraz o refundację kosztów wyposażenia lub doposażenia stanowiska pracy",
  request: "Wnoszę o przyznanie jednorazowo środków z Funduszu Pracy na podjęcie działalności gospodarczej opisanej we wniosku.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty.",
  deadlineLabel: "do 30 dni",
  deadline: "Starosta informuje o uwzględnieniu lub odmowie uwzględnienia wniosku w terminie 30 dni od złożenia kompletnego wniosku (§ 3 ust. 5 rozporządzenia).",
  sections: [
    {
      code: "nabor",
      title: "Nabór i kategoria wnioskodawcy",
      fields: [
        feeInfo(
          "infoNabor",
          "Powiatowy urząd pracy przyjmuje wnioski w okresach naboru ogłaszanych na swojej stronie — przed złożeniem wniosku sprawdź termin naboru, regulamin i dodatkowe warunki PUP (art. 147 ust. 2 ustawy). Dofinansowanie nie może przekroczyć 6-krotności przeciętnego wynagrodzenia, a rozlicza się tylko wydatki poniesione od dnia zawarcia umowy z PUP (§ 4 ust. 4 rozporządzenia).",
        ),
        {
          code: "kategoria",
          type: "radio",
          label: "Wnioskuję jako",
          required: true,
          options: [
            { value: "bezrobotny", label: "osoba bezrobotna zarejestrowana w powiatowym urzędzie pracy" },
            { value: "absolwent", label: "absolwent centrum integracji społecznej (CIS) albo klubu integracji społecznej (KIS)" },
            { value: "opiekun", label: "poszukujący pracy opiekun osoby niepełnosprawnej, niezatrudniony i niewykonujący innej pracy zarobkowej" },
          ],
        },
      ],
    },
    applicantSection(),
    {
      code: "daneUzupelniajace",
      title: "Dane uzupełniające wnioskodawcy",
      fields: [
        {
          code: "adresDoreczenElektronicznych",
          type: "text",
          label: "Adres do doręczeń elektronicznych (jeśli posiadasz)",
          maxLength: 100,
          placeholder: "AE:PL-00000-00000-00000-00",
        },
        {
          code: "stanCywilny",
          type: "radio",
          label: "Stan cywilny i wspólność majątkowa małżeńska",
          required: true,
          options: [
            { value: "brakZwiazku", label: "nie pozostaję w związku małżeńskim" },
            { value: "wspolnosc", label: "pozostaję w związku małżeńskim we wspólności majątkowej" },
            { value: "rozdzielnosc", label: "pozostaję w związku małżeńskim bez wspólności majątkowej" },
          ],
        },
        {
          code: "rachunek",
          type: "text",
          label: "Numer rachunku bankowego wnioskodawcy",
          maxLength: 40,
          placeholder: "00 0000 0000 0000 0000 0000 0000",
          hint: "Rachunek, którego jesteś właścicielem lub współwłaścicielem — PUP przekaże na niego środki po zawarciu umowy.",
        },
      ],
    },
    {
      code: "opisDzialalnosci",
      title: "Opis planowanej działalności",
      fields: [
        { code: "pkd", type: "text", label: "Symbol podklasy PKD", required: true, width: "third", maxLength: 10, placeholder: "np. 43.32.Z" },
        { code: "pkdNazwa", type: "text", label: "Nazwa podklasy PKD", width: "twoThirds", maxLength: 300 },
        { code: "pkdPozostale", type: "text", label: "Pozostałe kody PKD", maxLength: 300 },
        {
          code: "opis",
          type: "textarea",
          label: "Opis planowanej działalności, w tym niezbędne uprawnienia, pozwolenia, licencje lub koncesje",
          required: true,
          maxLength: 5000,
        },
        {
          code: "terminRozpoczecia",
          type: "text",
          label: "Planowany termin rozpoczęcia działalności",
          width: "half",
          maxLength: 100,
          hint: "Działalność rozpoczyna się w ciągu 30 dni od otrzymania środków (§ 4 ust. 2 rozporządzenia).",
        },
        { code: "mobilna", type: "checkbox", label: "Działalność będzie wykonywana mobilnie (bez stałego lokalu)" },
        {
          code: "adresDzialalnosci",
          type: "text",
          label: "Adres stałego miejsca wykonywania działalności (przy działalności mobilnej — adres przechowywania składników majątkowych zakupionych z dofinansowania)",
          required: true,
          maxLength: 300,
        },
        {
          code: "tytulLokalu",
          type: "radio",
          label: "Tytuł prawny do lokalu",
          required: true,
          visibleWhen: NOT_MOBILE,
          options: [
            { value: "wlasny", label: "lokal własny" },
            { value: "najem", label: "lokal wynajmowany lub dzierżawiony" },
            { value: "uzyczenie", label: "lokal użyczony" },
          ],
        },
        {
          code: "opisLokalu",
          type: "textarea",
          label: "Opis lokalu, w którym będzie wykonywana działalność",
          required: true,
          visibleWhen: NOT_MOBILE,
          maxLength: 2000,
        },
        {
          code: "kwalifikacje",
          type: "textarea",
          label: "Wykształcenie, ukończone szkolenia, doświadczenie zawodowe i umiejętności przydatne do wykonywania planowanej działalności",
          required: true,
          maxLength: 3000,
        },
        {
          code: "srodkiTechniczne",
          type: "textarea",
          label: "Posiadane środki techniczne potrzebne do prowadzenia działalności (maszyny, urządzenia, środki transportu)",
          maxLength: 2000,
        },
        {
          code: "analizaRynku",
          type: "textarea",
          label: "Analiza rynku — odbiorcy, dostawcy, konkurencja, sposób wejścia na rynek i promocji",
          maxLength: 5000,
          hint: "Nieobowiązkowo — część PUP wymaga jej w regulaminie.",
        },
      ],
    },
    {
      code: "przychodyKoszty",
      title: "Szacowane przychody i koszty w pierwszym roku działalności (miesięcznie)",
      fields: [
        { code: "przychodyMiesiecznie", type: "number", label: "Przychody ogółem — przeciętny miesiąc [zł]", required: true, width: "half", decimals: 2, min: 0 },
        { code: "kosztyMiesiecznie", type: "number", label: "Koszty ogółem — przeciętny miesiąc [zł]", required: true, width: "half", decimals: 2, min: 0 },
        {
          code: "strukturaKosztow",
          type: "textarea",
          label: "Struktura kosztów (np. towary i materiały, ZUS, czynsz, media, transport, księgowość, reklama)",
          maxLength: 2000,
        },
        { code: "uzasadnienieZalozen", type: "textarea", label: "Uzasadnienie przyjętych założeń", required: true, maxLength: 3000 },
      ],
    },
    {
      code: "specyfikacjaWydatkow",
      title: "Szczegółowa specyfikacja wydatków",
      fields: [
        expensesField({
          code: "wydatki",
          label: "Wydatki do poniesienia w ramach dofinansowania",
          hint: "Tylko zakupy niezbędne do podjęcia działalności: środki trwałe, urządzenia, maszyny, materiały, towary, materiały reklamowe, wartości niematerialne i prawne, dostosowanie lokalu, usługi oraz koszty pomocy prawnej, konsultacji i doradztwa (§ 2 ust. 3 pkt 9 rozporządzenia).",
          withCondition: true,
        }),
      ],
    },
    {
      code: "kalkulacja",
      title: "Kalkulacja kosztów i źródła finansowania",
      fields: [
        { code: "kwotaWnioskowana", type: "number", label: "Kwota wnioskowanego dofinansowania [zł]", required: true, width: "third", decimals: 2, min: 0 },
        { code: "srodkiWlasne", type: "number", label: "Środki własne [zł]", width: "third", decimals: 2, min: 0 },
        { code: "inneZrodlaKwota", type: "number", label: "Inne źródła (kredyty, pożyczki) [zł]", width: "third", decimals: 2, min: 0 },
        { code: "inneZrodlaOpis", type: "text", label: "Jakie inne źródła finansowania", maxLength: 300 },
      ],
    },
    {
      code: "zabezpieczenieUmowy",
      title: "Zabezpieczenie umowy",
      fields: [securityField()],
    },
    {
      code: "oswiadczenia",
      title: "Oświadczenia wnioskodawcy",
      description: "Oświadczenia na dzień złożenia wniosku (§ 2 ust. 4–6 rozporządzenia) — ich zakres zależy od kategorii wnioskodawcy.",
      fields: [
        {
          code: "oswNiekaralnosc",
          type: "declaration",
          label:
            "Oświadczam, że w okresie ostatnich 2 lat nie byłam/byłem prawomocnie skazana/skazany za przestępstwo składania fałszywych zeznań lub oświadczeń, przestępstwo przeciwko wiarygodności dokumentów lub przeciwko obrotowi gospodarczemu i interesom majątkowym w obrocie cywilnoprawnym na podstawie Kodeksu karnego, za przestępstwo skarbowe na podstawie Kodeksu karnego skarbowego lub za odpowiedni czyn zabroniony określony w przepisach prawa obcego.",
          required: true,
        },
        {
          code: "oswDzialalnoscKraj",
          type: "declaration",
          label:
            "Oświadczam, że w okresie ostatnich 12 miesięcy nie wykonywałam/wykonywałem działalności gospodarczej na terytorium Rzeczypospolitej Polskiej i nie pozostawałam/pozostawałem w okresie zawieszenia wykonywania działalności gospodarczej.",
          required: true,
          visibleWhen: { any: [UNEMPLOYED, GRADUATE] },
        },
        {
          code: "oswDzialalnoscZagranica",
          type: "declaration",
          label: "Oświadczam, że nie wykonuję za granicą działalności gospodarczej i nie pozostaję w okresie zawieszenia wykonywania tej działalności.",
          required: true,
          visibleWhen: { any: [UNEMPLOYED, GRADUATE] },
        },
        {
          code: "oswBrakDzialalnosci",
          type: "declaration",
          label: "Oświadczam, że nie wykonuję działalności gospodarczej i nie pozostaję w okresie zawieszenia wykonywania działalności gospodarczej.",
          required: true,
          visibleWhen: CARER,
        },
        {
          code: "oswSrodkiPubliczne",
          type: "declaration",
          label:
            "Oświadczam, że nie skorzystałam/skorzystałem z bezzwrotnych środków publicznych na podjęcie działalności gospodarczej, na założenie spółdzielni socjalnej lub na przystąpienie do spółdzielni socjalnej.",
          required: true,
        },
        {
          code: "oswUmorzenie",
          type: "declaration",
          label: "Oświadczam, że nie skorzystałam/skorzystałem z umorzenia pożyczki, o którym mowa w art. 187 ustawy o rynku pracy i służbach zatrudnienia.",
          required: true,
        },
        {
          code: "oswPrzerwanie",
          type: "declaration",
          label:
            "Oświadczam, że w okresie ostatnich 12 miesięcy nie przerwałam/przerwałem z własnej winy realizacji formy pomocy określonej w ustawie o rynku pracy i służbach zatrudnienia.",
          required: true,
          visibleWhen: { any: [UNEMPLOYED, CARER] },
        },
        {
          code: "oswInnyStarosta",
          type: "declaration",
          label:
            "Oświadczam, że nie złożyłam/złożyłem do innego starosty wniosku o dofinansowanie podjęcia działalności gospodarczej ani wniosku o środki na założenie spółdzielni socjalnej lub na przystąpienie do spółdzielni socjalnej.",
          required: true,
        },
        penaltyDeclaration(),
      ],
    },
    replySection(),
  ],
  attachments: [
    ...deMinimisAttachments(),
    {
      code: "oswiadczeniaPoreczycieli",
      label: "Oświadczenia poręczycieli o dochodach i zobowiązaniach finansowych",
      hint: "Na druku PUP (§ 10 ust. 4 rozporządzenia) — część urzędów wymaga ich już z wnioskiem.",
      visibleWhen: SURETY,
      multiple: true,
    },
    {
      code: "zgodaMalzonka",
      label: "Zgoda małżonka wnioskodawcy na zawarcie umowy",
      hint: "Praktyka PUP przy wspólności majątkowej.",
      visibleWhen: { field: "stanCywilny", equals: "wspolnosc" },
    },
    {
      code: "dokumentLokalu",
      label: "Umowa najmu, dzierżawy lub użyczenia lokalu (albo umowa przedwstępna) lub dokument potwierdzający tytuł prawny do lokalu",
      hint: "Praktyka PUP.",
      visibleWhen: NOT_MOBILE,
    },
    {
      code: "dokumentyKwalifikacji",
      label: "Dokumenty potwierdzające kwalifikacje lub doświadczenie w dziedzinie planowanej działalności",
      hint: "Świadectwa, dyplomy, zaświadczenia o szkoleniach, świadectwa pracy — jeśli nie zostały złożone w PUP przy rejestracji (praktyka PUP).",
      multiple: true,
    },
    {
      code: "ofertyCenowe",
      label: "Oferty cenowe, zdjęcia sprzętu używanego",
      hint: "Praktyka PUP.",
      multiple: true,
    },
    {
      code: "dokumentyUprawnien",
      label: "Dokumenty uprawnień, zezwoleń, koncesji lub licencji",
      hint: "Gdy planowana działalność ich wymaga (praktyka PUP).",
      multiple: true,
    },
    {
      code: "dokumentZabezpieczenia",
      label: "Dokument ustanowienia zabezpieczenia (blokada środków, gwarancja bankowa, akt notarialny)",
      hint: "Dostarczasz go przed zawarciem umowy — dołącz teraz, jeśli już go masz.",
      visibleWhen: SECURITY_DOCUMENT,
    },
    otherAttachments(),
  ],
};

export default H6;
